import { Device } from "@/types/device";
import { supabase } from "@/integrations/supabase/client";

export interface HealthMetric {
  name: string;
  score: number;
  maxScore: number;
  weight: number;
  status: "excellent" | "good" | "fair" | "poor";
  description: string;
  recommendations?: string[];
}

export interface DeviceHealthScore {
  overall: number;
  grade: "A+" | "A" | "B+" | "B" | "C+" | "C" | "D" | "F";
  metrics: HealthMetric[];
  lastUpdated: string;
}

export interface HealthTrend {
  currentScore: number;
  previousScore: number;
  change: number;
  percentChange: number;
  trend: 'up' | 'down' | 'stable';
  daysCompared: number;
}

export interface HealthHistoryPoint {
  date: string;
  score: number;
  grade: string;
  metrics: HealthMetric[];
}

// OS version mapping for compliance scoring
const OS_VERSION_SCORES = {
  macOS: {
    "14.4": 100, "14.3": 90, "14.2": 85, "14.1": 80, "14.0": 75,
    "13.6": 60, "13.5": 55, "13.4": 50, "13.3": 45, "13.2": 40,
    "12.7": 30, "12.6": 25, "12.5": 20, "12.4": 15, "12.3": 10,
    "11.7": 5
  },
  iOS: {
    "17.4": 100, "17.3": 90, "17.2": 85, "17.1": 80, "17.0": 75,
    "16.7": 60, "16.6": 55, "16.5": 50, "16.4": 45,
    "15.8": 30, "15.7": 25
  },
  iPadOS: {
    "17.4": 100, "17.3": 90, "17.2": 85, "17.1": 80, "17.0": 75,
    "16.7": 60, "16.6": 55, "16.5": 50, "16.4": 45,
    "15.8": 30, "15.7": 25
  }
};

export function calculateOSCompliance(device: Device): HealthMetric {
  const osType = device.os_type as keyof typeof OS_VERSION_SCORES;
  const versionScores = OS_VERSION_SCORES[osType] || {};
  
  // Return low score if os_version is not available
  if (!device.os_version) {
    return {
      name: 'OS Compliance',
      score: 0,
      maxScore: 100,
      weight: 0.25,
      status: 'poor',
      description: 'OS version information not available',
      recommendations: ['Update device to send OS version information']
    };
  }
  
  // Extract version number from version string
  const versionMatch = device.os_version.match(/(\d+\.\d+)/);
  const version = versionMatch ? versionMatch[1] : device.os_version;
  const [major, minor] = version.split('.').map(Number);
  
  let score: number = versionScores[version as keyof typeof versionScores] ?? 0;
  
  // Smart fallback: if version not found, check if it's newer than known versions
  if (score === 0 && !isNaN(major) && !isNaN(minor)) {
    if (osType === 'macOS') {
      const latestKnownVersion = 14.4;
      const currentVersion = major + (minor / 10);
      if (currentVersion >= latestKnownVersion) {
        score = 100;
      }
    } else if (osType === 'iOS' || osType === 'iPadOS') {
      const latestKnownVersion = 17.4;
      const currentVersion = major + (minor / 10);
      if (currentVersion >= latestKnownVersion) {
        score = 100;
      }
    }
  }
  
  let status: HealthMetric["status"];
  let recommendations: string[] = [];
  
  if (score >= 90) {
    status = "excellent";
  } else if (score >= 75) {
    status = "good";
    recommendations.push("Consider updating to the latest OS version");
  } else if (score >= 50) {
    status = "fair";
    recommendations.push("OS version is outdated - update recommended");
  } else {
    status = "poor";
    recommendations.push("OS version is critically outdated - immediate update required");
  }

  return {
    name: "OS Compliance",
    score,
    maxScore: 100,
    weight: 0.25,
    status,
    description: `Running ${device.os_type} ${device.os_version}`,
    recommendations
  };
}

export function calculateSecurityPatches(device: Device): HealthMetric {
  // Simulate security patch status based on OS version and last seen
  const lastSeen = new Date(device.last_seen);
  const now = new Date();
  const daysSinceLastSeen = Math.floor((now.getTime() - lastSeen.getTime()) / (1000 * 60 * 60 * 24));
  
  let score = 100;
  let status: HealthMetric["status"] = "excellent";
  let recommendations: string[] = [];
  
  // Reduce score based on days since last check-in
  if (daysSinceLastSeen > 7) {
    score -= 20;
    status = "good";
    recommendations.push("Device hasn't checked in recently");
  }
  
  if (daysSinceLastSeen > 14) {
    score -= 30;
    status = "fair";
    recommendations.push("Device offline for extended period");
  }
  
  if (daysSinceLastSeen > 30) {
    score -= 40;
    status = "poor";
    recommendations.push("Device may have missed critical security updates");
  }
  
  // Factor in OS version for security patches
  const osCompliance = calculateOSCompliance(device);
  if (osCompliance.score < 75) {
    score = Math.min(score, 60);
    if (osCompliance.score < 50) {
      score = Math.min(score, 30);
    }
  }
  
  score = Math.max(0, score);

  return {
    name: "Security Patches",
    score,
    maxScore: 100,
    weight: 0.30,
    status,
    description: `Last security check: ${daysSinceLastSeen} days ago`,
    recommendations
  };
}

export function calculateEncryptionStatus(device: Device): HealthMetric {
  // Simulate encryption status - in real implementation, this would come from device data
  // For now, newer devices get better encryption scores
  let score = 100;
  let status: HealthMetric["status"] = "excellent";
  let recommendations: string[] = [];
  
  // Check if os_version is available before using it
  if (device.os_version) {
    const isOlderDevice = device.os_version.includes("Big Sur") || 
                         device.os_version.includes("Monterey") ||
                         device.os_version.includes("16.") ||
                         device.os_version.includes("15.");
    
    if (isOlderDevice) {
      score = 75;
      status = "good";
      recommendations.push("Consider enabling advanced encryption features");
    }
  } else {
    // No OS version available
    score = 50;
    status = "fair";
    recommendations.push("OS version information not available");
  }
  
  // Pending devices might not have encryption configured yet
  if (device.status === "pending") {
    score = 50;
    status = "fair";
    recommendations.push("Complete device enrollment to enable encryption");
  }

  return {
    name: "Encryption",
    score,
    maxScore: 100,
    weight: 0.25,
    status,
    description: "FileVault/Device encryption status",
    recommendations
  };
}

export function calculateBatteryHealth(device: Device): HealthMetric {
  let score = 100;
  let status: HealthMetric["status"] = "excellent";
  let recommendations: string[] = [];
  
  if (device.battery_level === null || device.battery_level === undefined) {
    score = 50;
    status = "fair";
    recommendations.push("Battery information not available");
  } else {
    // Score based on battery level and status
    if (device.battery_level < 20) {
      score = 40;
      status = "poor";
      recommendations.push("Battery critically low - charge immediately");
    } else if (device.battery_level < 50) {
      score = 70;
      status = "fair";
      recommendations.push("Battery level low - consider charging");
    } else if (device.battery_level < 80) {
      score = 85;
      status = "good";
    }
    
    // Factor in battery status
    if (device.battery_status === "unknown") {
      score -= 20;
      recommendations.push("Battery status unknown - check device");
    }
  }
  
  score = Math.max(0, score);

  return {
    name: "Battery Health",
    score,
    maxScore: 100,
    weight: 0.10,
    status,
    description: device.battery_level ? `${device.battery_level}% (${device.battery_status})` : "Unknown",
    recommendations
  };
}

export function calculateAppCompliance(device: Device): HealthMetric {
  let score = 100;
  let status: HealthMetric["status"] = "excellent";
  let recommendations: string[] = [];
  
  const installedApps = device.installed_apps || [];
  const appCount = installedApps.length;
  
  if (appCount === 0 && device.status !== "pending") {
    score = 30;
    status = "poor";
    recommendations.push("No applications detected - check device inventory");
  } else if (appCount < 5) {
    score = 60;
    status = "fair";
    recommendations.push("Limited applications installed");
  } else if (appCount > 50) {
    score = 80;
    status = "good";
    recommendations.push("Large number of apps - ensure all are approved");
  }

  return {
    name: "App Compliance",
    score,
    maxScore: 100,
    weight: 0.10,
    status,
    description: `${appCount} applications installed`,
    recommendations
  };
}

export function calculateDeviceHealthScore(device: Device): DeviceHealthScore {
  const metrics = [
    calculateOSCompliance(device),
    calculateSecurityPatches(device),
    calculateEncryptionStatus(device),
    calculateBatteryHealth(device),
    calculateAppCompliance(device)
  ];
  
  // Calculate weighted overall score
  const overall = metrics.reduce((total, metric) => {
    return total + (metric.score * metric.weight);
  }, 0);
  
  // Determine grade based on overall score
  let grade: DeviceHealthScore["grade"];
  if (overall >= 95) grade = "A+";
  else if (overall >= 90) grade = "A";
  else if (overall >= 85) grade = "B+";
  else if (overall >= 80) grade = "B";
  else if (overall >= 75) grade = "C+";
  else if (overall >= 70) grade = "C";
  else if (overall >= 60) grade = "D";
  else grade = "F";
  
  return {
    overall: Math.round(overall),
    grade,
    metrics,
    lastUpdated: new Date().toISOString()
  };
}

export function getHealthScoreColor(score: number): string {
  if (score >= 90) return "hsl(var(--chart-5))"; // Green
  if (score >= 75) return "hsl(var(--chart-2))"; // Teal
  if (score >= 60) return "hsl(var(--chart-3))"; // Orange
  return "hsl(var(--chart-6))"; // Red
}

export function getStatusColor(status: HealthMetric["status"]): string {
  switch (status) {
    case "excellent": return "hsl(var(--chart-5))";
    case "good": return "hsl(var(--chart-2))";
    case "fair": return "hsl(var(--chart-3))";
    case "poor": return "hsl(var(--chart-6))";
    default: return "hsl(var(--muted-foreground))";
  }
}

// Save device health score to history
export async function saveDeviceHealthScore(
  deviceId: string,
  healthScore: DeviceHealthScore
): Promise<void> {
  try {
    // Check if we already have a record for today
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const { data: existingRecord } = await supabase
      .from('device_health_history')
      .select('id')
      .eq('device_id', deviceId)
      .gte('created_at', today.toISOString())
      .maybeSingle();
    
    // Only save if no record exists for today
    if (!existingRecord) {
      const { error } = await supabase
        .from('device_health_history')
        .insert([{
          device_id: deviceId,
          overall_score: healthScore.overall,
          grade: healthScore.grade,
          metrics: healthScore.metrics as any,
        }]);
      
      if (error) {
        console.error('Error saving device health score:', error);
      }
    }
  } catch (error) {
    console.error('Error saving device health score:', error);
  }
}

// Get health score trend compared to previous period
export async function getHealthScoreTrend(
  deviceId: string,
  daysBack: number = 7
): Promise<HealthTrend | null> {
  try {
    // Get the most recent score (current)
    const { data: currentData } = await supabase
      .from('device_health_history')
      .select('overall_score, created_at')
      .eq('device_id', deviceId)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    
    if (!currentData) {
      return null;
    }
    
    // Get score from daysBack ago
    const compareDate = new Date();
    compareDate.setDate(compareDate.getDate() - daysBack);
    
    const { data: previousData } = await supabase
      .from('device_health_history')
      .select('overall_score')
      .eq('device_id', deviceId)
      .lte('created_at', compareDate.toISOString())
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    
    if (!previousData) {
      // No historical data to compare
      return null;
    }
    
    const currentScore = currentData.overall_score;
    const previousScore = previousData.overall_score;
    const change = currentScore - previousScore;
    const percentChange = previousScore !== 0 
      ? Math.round((change / previousScore) * 100) 
      : 0;
    
    let trend: 'up' | 'down' | 'stable';
    if (Math.abs(change) < 2) {
      trend = 'stable';
    } else if (change > 0) {
      trend = 'up';
    } else {
      trend = 'down';
    }
    
    return {
      currentScore,
      previousScore,
      change,
      percentChange,
      trend,
      daysCompared: daysBack,
    };
  } catch (error) {
    console.error('Error getting health score trend:', error);
    return null;
  }
}

// Get health score history for charts
export async function getHealthHistory(
  deviceId: string,
  days: number = 30
): Promise<HealthHistoryPoint[]> {
  try {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);
    
    const { data, error } = await supabase
      .from('device_health_history')
      .select('overall_score, grade, metrics, created_at')
      .eq('device_id', deviceId)
      .gte('created_at', startDate.toISOString())
      .order('created_at', { ascending: true });
    
    if (error) {
      console.error('Error fetching health history:', error);
      return [];
    }
    
    return (data || []).map(record => ({
      date: new Date(record.created_at).toISOString().split('T')[0],
      score: record.overall_score,
      grade: record.grade,
      metrics: (record.metrics as any) as HealthMetric[],
    }));
  } catch (error) {
    console.error('Error fetching health history:', error);
    return [];
  }
}